import { deriveOriginalDueDate } from '../lib/payment-terms';
import type { InvoiceRow } from '../types';
import type { AdvanceSubmitValues } from './downpayment-invoice-schema';

export interface TicketContextForDownpayment {
  readonly currency: 'USD' | 'EUR' | null;
  readonly counterpartyName: string | null;
  readonly downpaymentAmount: string | null;
  readonly downpaymentDueDate: string | null;
}

export function isoDateValue(d: Date): string {
  const yyyy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`);
}

function defaultsFromExisting(
  side: 'buy' | 'sell',
  existing: InvoiceRow,
  ticket: TicketContextForDownpayment,
): AdvanceSubmitValues {
  const due =
    existing.original_due_date === null
      ? parseIsoDate(deriveOriginalDueDate({ downpaymentDueDate: ticket.downpaymentDueDate }))
      : parseIsoDate(existing.original_due_date.slice(0, 10));
  const base = {
    invoice_number: existing.invoice_number ?? '',
    currency: (existing.currency ?? 'USD') as 'USD' | 'EUR',
    total_amount: existing.total_amount === null ? 0 : Number(existing.total_amount),
    vat_amount: existing.vat_amount === null ? 0 : Number(existing.vat_amount),
    original_due_date: due,
    applied_downpayment_amount:
      existing.applied_downpayment_amount === null
        ? 0
        : Number(existing.applied_downpayment_amount),
  };
  const result: AdvanceSubmitValues = { ...base };
  if (side === 'buy' && existing.issuer_company_name !== null) {
    result.issuer_company_name = existing.issuer_company_name;
  }
  if (existing.company_name !== null) {
    result.company_name = existing.company_name;
  }
  return result;
}

function defaultsForNew(
  side: 'buy' | 'sell',
  ticket: TicketContextForDownpayment,
): AdvanceSubmitValues {
  const dueIso = deriveOriginalDueDate({ downpaymentDueDate: ticket.downpaymentDueDate });
  const result: AdvanceSubmitValues = {
    invoice_number: '',
    currency: ticket.currency ?? 'USD',
    total_amount: ticket.downpaymentAmount === null ? 0 : Number(ticket.downpaymentAmount),
    vat_amount: 0,
    original_due_date: parseIsoDate(dueIso),
    applied_downpayment_amount: 0,
  };
  if (ticket.counterpartyName !== null) {
    result.company_name = ticket.counterpartyName;
    if (side === 'buy') {
      result.issuer_company_name = ticket.counterpartyName;
    }
  }
  return result;
}

export function buildDefaults(
  side: 'buy' | 'sell',
  existing: InvoiceRow | null,
  ticket: TicketContextForDownpayment,
): AdvanceSubmitValues {
  if (existing !== null) return defaultsFromExisting(side, existing, ticket);
  return defaultsForNew(side, ticket);
}

export interface SaveContext {
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly aiExtractionId?: string;
  // The advance's coverage set, set in the create dialog's picker. Included
  // only on create; omitted = the server splits across every live BL
  // quantity-ratio.
  readonly coverage?: readonly { bl_order_id: string; amount: number }[];
}

export function buildSavePayload(
  values: AdvanceSubmitValues,
  ctx: SaveContext,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    invoice_number: values.invoice_number,
    currency: values.currency,
    total_amount: values.total_amount,
    vat_amount: values.vat_amount,
    original_due_date: isoDateValue(values.original_due_date),
    applied_downpayment_amount: values.applied_downpayment_amount,
  };
  if (values.company_name !== undefined) base['company_name'] = values.company_name;
  if (ctx.side === 'buy' && values.issuer_company_name !== undefined) {
    base['issuer_company_name'] = values.issuer_company_name;
  }
  if (ctx.existingInvoice !== null) return base;
  const out: Record<string, unknown> = {
    order_id: ctx.orderId,
    invoice_direction: ctx.side === 'buy' ? 'Purchase' : 'Sale',
    invoice_type: 'Advance',
    ...base,
  };
  if (ctx.aiExtractionId !== undefined) out['ai_extraction_id'] = ctx.aiExtractionId;
  if (ctx.coverage !== undefined && ctx.coverage.length > 0) out['coverage'] = ctx.coverage;
  return out;
}

export interface AdvancePrefill {
  readonly values: Partial<AdvanceSubmitValues>;
  readonly aiExtractionId: string;
  // The PDF the extraction ran on, seeding the dialog's attach pane so upload
  // and entry stay one surface.
  readonly file?: File;
}

export function applyPrefill(
  defaults: AdvanceSubmitValues,
  prefill: AdvancePrefill | undefined,
): AdvanceSubmitValues {
  if (prefill === undefined) return defaults;
  return { ...defaults, ...prefill.values };
}
